import React, { FC, useMemo, useRef, useState } from 'react'
import Modal from '../common/Modal'
import { secToHour, secToMinute } from '../../../utils/date'
import Tips from '../common/Tips'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { RequestPostTaskBulkLabeling } from '../../../states/types/task'
import { uniqueId } from 'lodash'

interface GroupedTask {
  gt_id: number
  ids: number[]
  type: 'ActiveTask' | 'ManualTask' | 'OnlineMeetingTask'
  service_name: string | null
  icon: string | null
  icon_color: string | null
  name: string | null
  file_path: string | null
  included_url_parts: string | null
  time: number
  count: number
  labeling_status: 'single' | 'mixed' | 'none'
  label_group_id: number | null
  label_group_name: string | null
  label_group_color: string | null
  label_id: number | null
  label_name: string | null
  label_color: string | null
  contains_locked_task: boolean
}

interface LabelGroup {
  id: number
  name: string
  color: string
  code: string | null
}

interface Label {
  id: number
  name: string
  color: string
  description: string | null
}

interface Props {
  targetList: GroupedTask[]
  labelGroup: LabelGroup | undefined
  labelList: Label[]
  closeCallback: VoidFunction
  updateCallback: (params: RequestPostTaskBulkLabeling) => void
  deleteCallback: (taskIds: number[]) => void
  isDisplay: boolean
  closeable?: boolean
}

const BulkLabelingModal: FC<Props> = (props) => {
  const {
    targetList,
    labelGroup,
    labelList,
    closeCallback,
    updateCallback,
    deleteCallback,
    isDisplay,
    closeable,
  } = props

  const RESERVED_LABEL_GROUP_ID = 2

  const elementRef = useRef<HTMLDivElement>(null)

  const [selectLabelId, setSelectLabelId] = useState<number>()
  const [isOpenSelectLabelModal, setIsOpenSelectLabelModal] = useState<boolean>(false)

  const [labelDescription, setLabelDescription] = useState<string | null>("")
  const [descriptionPosition, setDescriptionPosition] = useState<{x: number, y: number, reverse: boolean}>({ x: 0, y: 0, reverse: false })
  const [displayDescription, setDisplayDescription] = useState<boolean>(false)

  const selectLabel = useMemo(() => {
    if (selectLabelId === undefined) return undefined
    if (selectLabelId <= RESERVED_LABEL_GROUP_ID) {
      let tmpName = "プロジェクトに属さない作業データ"
      if (selectLabelId === 1) {
        tmpName = "休憩"
      } else if (selectLabelId === 2) {
        tmpName = "業務外"
      }
      return ({
        label_group_id: selectLabelId,
        label_group_name: tmpName,
        label_group_color: "",
        label_id: selectLabelId,
        label_name: tmpName,
        label_color: "",
      })
    }
    const tmpLabel = labelList?.find((v) => v.id === selectLabelId)
    return ({
      label_group_id: labelGroup?.id,
      label_group_name: labelGroup?.name,
      label_group_color: labelGroup?.color,
      label_id: tmpLabel?.id,
      label_name: tmpLabel?.name,
      label_color: tmpLabel?.color,
    })
  }, [selectLabelId])

  const includingLocked = useMemo(() => (
    !!targetList.find((v) => v.contains_locked_task)
  ), [targetList])

  const hideLabelDescription = () => {
    setDisplayDescription(false)
    setLabelDescription("")
  }

  const displayLabelDescription = (text: string | null, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!text) {
      hideLabelDescription()
      return
    }
    setDisplayDescription(true)
    setLabelDescription(text)
    const xPosition = event.clientX - (elementRef.current?.getBoundingClientRect().x || 0)
    setDescriptionPosition({
      x: xPosition - 80,
      y: event.clientY - (elementRef.current?.getBoundingClientRect().y || 0) + 100,
      reverse: false,
    })
  }

  const reset = () => {
    setSelectLabelId(undefined)
    setIsOpenSelectLabelModal(false)
    hideLabelDescription()
  }

  const closeModal = () => {
    closeCallback()
    reset()
  }

  const clickLabel = (labelId: number) => {
    setSelectLabelId(labelId)
    setIsOpenSelectLabelModal(false)
    hideLabelDescription()
  }

  const submit = () => {
    const params: RequestPostTaskBulkLabeling = {
      grouped_task_ids: targetList.map((v) => v.ids).flat(),
      label_id: selectLabelId || 0,
    }
    updateCallback(params)
    closeModal()
  }

  const notLabeling = () => {
    deleteCallback(targetList.map((v) => v.ids).flat())
    closeModal()
  }

  const buildTime = (time: number) => (
    <>
      {
        time >= 60 ? (
          <>
            {!!secToHour(time) && `${secToHour(time)}時間`}
            {!!secToMinute(time) && `${secToMinute(time)}分`}
          </>
        ) : (
          <>
            1分未満
          </>
        )
      }
    </>
  )

  return (
    <>
      <Modal
        show={isDisplay}
        wrapperClass="multi_approved_modal w900"
        closeCallback={closeModal}
        closeable={closeable}
      >
        <>
          <div className="multi_approved_modal_title">
            作業データ一括仕分け
          </div>
          <div
            className="multi_approved_modal_main"
            ref={elementRef}
          >
            <div className="description">
              <div>
                対象の作業データを一括仕分けします
              </div>
              {
                targetList.length > 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "2px",
                    }}
                  >
                    合計
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#228b22",
                      }}
                    >
                      {targetList.length}
                    </div>
                    件 ( 合計時間
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#228b22",
                      }}
                    >
                      {buildTime(targetList.reduce((s, v) => s + v.time, 0) || 0)}
                    </div>
                    ) の作業データが選択されています
                  </div>
                )
              }
            </div>
            {
              displayDescription && (
                <div
                  style={{
                    position: "absolute",
                    top: `${descriptionPosition.y}px`,
                    left: descriptionPosition.reverse ? "unset" : `${descriptionPosition.x}px`,
                    right: descriptionPosition.reverse ? `${descriptionPosition.x}px` : "unset",
                    maxWidth: "500px",
                    backgroundColor: "#edfbfb",
                    padding: "8px 16px",
                    border: "solid 1px #4a91dd",
                    borderRadius: "4px",
                    zIndex: "2005",
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                  }}
                  onMouseLeave={() => hideLabelDescription()}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      position: "relative",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', 'comment-dots']} />
                    <FontAwesomeIcon
                      icon={['fas', 'times']}
                      onClick={() => hideLabelDescription()}
                      style={{
                        position: "absolute",
                        right: 0,
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <div>
                    {
                      labelDescription && labelDescription.split('\n').map((v) => (
                        <div
                          key={uniqueId()}
                          style={{
                            minHeight: "1em",
                          }}
                        >
                          {v}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )
            }
            <div
              style={{
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                marginBottom: "16px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                position: "relative",
              }}
            >
              対象の作業データを
              {
                selectLabel ? (
                  <>
                    <button
                      type="button"
                      style={{
                        border: "solid 1px #666666",
                        borderRadius: "4px",
                        padding: "2px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        position: "relative",
                        fontSize: "10px",
                        backgroundColor: "#FFFFFF",
                        color: "#666666",
                        pointerEvents: "initial",
                        userSelect: "none",
                      }}
                    >
                      {
                        (selectLabel.label_group_id || 0) > RESERVED_LABEL_GROUP_ID ? (
                          <>
                            <div
                              style={{
                                width: "8px",
                                height: "8px",
                                backgroundColor: selectLabel.label_group_color,
                              }}
                            />
                            {`${selectLabel.label_group_name}:${selectLabel.label_name || ""}`}
                          </>
                        ) : (
                          <>
                            {selectLabel.label_group_name}
                          </>
                        )
                      }
                      <span
                        style={{
                          marginLeft: "4px",
                          cursor: "pointer",
                        }}
                        onClick={reset}
                        role="none"
                      >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                      </span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsOpenSelectLabelModal(true)}
                      style={{
                        borderRadius: "4px",
                        padding: "6px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                        position: "relative",
                        fontSize: "10px",
                        backgroundColor: "#E44242",
                        color: "#ffffff",
                        pointerEvents: "initial",
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'tags']} rotation={90} />
                      選択してください
                    </button>
                    {
                      isOpenSelectLabelModal && (
                        <>
                          <div
                            style={{
                              position: "absolute",
                              top: "38px",
                              left: "0",
                              backgroundColor: "#FFFFFF",
                              borderRadius: "4px",
                              zIndex: "2004",
                            }}
                          >
                            <div
                              style={{
                                padding: "8px 16px",
                                backgroundColor: "#EEEEEE",
                                borderRadius: "4px 4px 0 0",
                              }}
                            >
                              業務を選択
                            </div>
                            <div
                              style={{
                                padding: "8px 16px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "right",
                                  gap: "4px",
                                  flexWrap: "wrap",
                                  padding: "8px 0px",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => clickLabel(1)}
                                  style={{
                                    borderRadius: "24px",
                                    backgroundColor: "#e88042",
                                    fontSize: "10px",
                                    color: "#ffffff",
                                    padding: "4px 8px",
                                  }}
                                >
                                  休憩
                                </button>
                                <button
                                  type="button"
                                  onClick={() => clickLabel(0)}
                                  style={{
                                    borderRadius: "24px",
                                    backgroundColor: "#0493b1",
                                    fontSize: "10px",
                                    color: "#ffffff",
                                    padding: "4px 8px",
                                  }}
                                >
                                  プロジェクトに属さない作業データ
                                </button>
                              </div>
                              <div
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#EEEEEE",
                                    borderRadius: "4px 4px 0 0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: labelGroup?.color,
                                      width: "12px",
                                      height: "12px",
                                    }}
                                  />
                                  {labelGroup?.name}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    flexWrap: "wrap",
                                    padding: "8px",
                                    maxWidth: "500px",
                                  }}
                                >
                                  {
                                    labelList.map((v) => (
                                      <React.Fragment key={`bulk_modal_${v.name}`}>
                                        <button
                                          type="button"
                                          key={v.name}
                                          style={{
                                            backgroundColor: "#ffffff",
                                            border: "solid 1px #cccccc",
                                            padding: "8px 10px",
                                            color: "#333333",
                                            borderRadius: "14px",
                                            margin: 0,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            fontSize: "11px",
                                          }}
                                          onClick={() => clickLabel(v.id)}
                                          onMouseMove={(e) => displayLabelDescription(v.description, e)}
                                          onMouseLeave={() => hideLabelDescription()}
                                        >
                                          <div
                                            style={{
                                              width: "12px",
                                              height: "12px",
                                              borderRadius: "50%",
                                              backgroundColor: v.color,
                                            }}
                                          />
                                          {v.name}
                                        </button>
                                      </React.Fragment>
                                    ))
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="close_screen gray" role="presentation" onClick={() => setIsOpenSelectLabelModal(false)} />
                        </>
                      )
                    }
                  </>
                )
              }
              に一括仕分けします
            </div>
            {
              includingLocked && (
                <div
                  style={{
                    border: "1px solid #e54242",
                    borderRadius: "4px",
                    marginBottom: "16px",
                    padding: "8px",
                    backgroundColor: "#ffeaea",
                    color: "#e54242",
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                  }}
                >
                  <div>
                    申請・承認済み作業データが一括仕分け対象に含まれています。
                  </div>
                  <div>
                    対象ユーザーのデータは未申請状態に差戻しされます。ご注意ください。
                  </div>
                </div>
              )
            }
            <div
              style={{
                maxHeight: "300px",
                overflowY: "scroll",
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                padding: "4px",
              }}
            >
              {
                targetList.map((v) => (
                  <React.Fragment key={`${v.name}_${v.service_name}`}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "4px",
                        borderBottom: "solid 1px #EEEEEE",
                        padding: "16px 8px 8px",
                        minHeight: "40px",
                        backgroundColor: "#FFFFFF",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <div
                            className="icon_area"
                            style={{
                              color: v.icon_color || (v.type !== "ActiveTask" && v.name ? "#008800" : "#FF0000"),
                            }}
                          >
                            {
                              v.icon ? (
                                <Tips
                                  description={`${v.service_name || ""}`}
                                  className="normal"
                                  icon={false}
                                >
                                  <FontAwesomeIcon
                                    icon={[v.icon.split(" ")[0] as IconPrefix, v.icon.split(" ")[1] as IconName]}
                                    fixedWidth
                                  />
                                </Tips>
                              ) : (
                                <>
                                  {
                                    v.type === "OnlineMeetingTask" ? (
                                      <FontAwesomeIcon
                                        icon={["fas", "video"]}
                                        fixedWidth
                                      />
                                    ) : (
                                      <FontAwesomeIcon
                                        icon={v.name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                        fixedWidth
                                      />
                                    )
                                  }
                                </>
                              )
                            }
                          </div>
                          <div
                            className="text_area"
                          >
                            {v.name || (v.type !== "ActiveTask" ? "設定されていません" : "")}
                            {
                              v.file_path && (
                                <div
                                  style={{
                                    color: "#999999",
                                    fontSize: "11px",
                                  }}
                                  title={`ファイルパス: ${v.file_path}`}
                                >
                                  {`(${v.file_path})`}
                                </div>
                              )
                            }
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "4px",
                        }}
                      >
                        <>
                          {
                            v.label_group_id === null && (
                              <div className="label_wrapper no_label">
                                <div
                                  className="label"
                                  style={{
                                    backgroundColor: v.labeling_status === "mixed" ? "#ffc20b" : "#FFFFFF",
                                    border: `solid 1px ${v.labeling_status === "mixed" ? "#000000" : "#e44242"}`,
                                    borderRadius: "4px",
                                    color: v.labeling_status === "mixed" ? "#000000" : "#e44242",
                                    fontSize: "10px",
                                    padding: "2px 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    pointerEvents: "initial",
                                    cursor: "default",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  { v.labeling_status === "mixed" ? "複数の仕分け" : "未仕分け"}
                                </div>
                              </div>
                            )
                          }
                          {
                            v.label_group_id !== null
                            && v.label_group_id > RESERVED_LABEL_GROUP_ID
                            && (
                              <div className="label_wrapper">
                                <div
                                  className="label"
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    border: "solid 1px #747474",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    padding: "2px 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    pointerEvents: "initial",
                                    cursor: "default",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      backgroundColor: v.label_group_color || "#CCCCCC",
                                    }}
                                  />
                                  {`${v.label_group_name}:${v.label_name || ""}`}
                                </div>
                              </div>
                            )
                          }
                          {
                            v.label_group_id !== null && v.label_group_id <= RESERVED_LABEL_GROUP_ID && (
                              <div className="label_wrapper">
                                <div
                                  className="label"
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    border: "solid 1px #747474",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    padding: "2px 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    pointerEvents: "initial",
                                    cursor: "default",
                                  }}
                                >
                                  {v.label_group_name}
                                </div>
                              </div>
                            )
                          }
                        </>
                        <div
                          className="time_area"
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "end",
                            gap: "2px",
                            color: "#999999",
                            fontSize: "10px",
                            minWidth: "140px",
                          }}
                        >
                          合計時間
                          <div
                            style={{
                              fontSize: "16px",
                            }}
                          >
                            {buildTime(v.time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))
              }
            </div>
            <div className="button_area">
              <button
                type="button"
                onClick={closeModal}
              >
                キャンセル
              </button>
              {
                selectLabel ? (
                  <button
                    type="button"
                    style={{
                      border: `solid 1px #507DC0`,
                      backgroundColor: "#507DC0",
                      color: "#FFFFFF",
                    }}
                    onClick={submit}
                  >
                    一括仕分けする
                  </button>
                ) : (
                  <button
                    type="button"
                    style={{
                      border: `solid 1px #e54242`,
                      backgroundColor: "#e54242",
                      color: "#FFFFFF",
                    }}
                    onClick={notLabeling}
                  >
                    未仕分けにする
                  </button>
                )
              }
            </div>
          </div>
        </>
      </Modal>
    </>
  )
}

BulkLabelingModal.defaultProps = {
  closeable: true,
}

export default BulkLabelingModal
